import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, toImg } from "@/api/client";
import PropertyCard from "@/components/PropertyCard";
import ContactForm from "@/components/ContactForm";
import SEO, { faqPageSchema, breadcrumbSchema } from "@/components/SEO";
import RichText from "@/components/RichText";
import { TrendingUp } from "lucide-react";

const fmtEur = (n) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

function placeSchema(zone, canonical) {
  const s = {
    "@context": "https://schema.org",
    "@type": "Place",
    name: zone.name,
    description: zone.short_intro || zone.full_description,
    url: canonical,
    address: {
      "@type": "PostalAddress",
      addressLocality: zone.name,
      addressRegion: "Porto",
      addressCountry: "PT",
    },
  };
  if (zone.avg_price_sqm) {
    s.additionalProperty = [
      {
        "@type": "PropertyValue",
        name: "Preço médio por m² (apartamentos à venda)",
        value: zone.avg_price_sqm,
        unitCode: "EUR",
        unitText: "EUR por m²",
        valueReference: zone.avg_price_sqm_source || "Idealista",
      },
    ];
  }
  return s;
}

export default function ZonaDetailPage() {
  const { slug } = useParams();
  const [zone, setZone] = useState(null);
  const [properties, setProperties] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setZone(null);
    setNotFound(false);
    api.get(`/zones/${slug}`).then((r) => setZone(r.data)).catch(() => setNotFound(true));
    api.get("/properties", { params: { zone: slug, limit: 6 } }).then((r) => setProperties(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-cnp py-24 text-center">
        <h1 className="font-display text-3xl">Zona não encontrada</h1>
        <Link to="/zonas" className="btn-secondary mt-6 inline-flex">Ver todas as zonas</Link>
      </div>
    );
  }
  if (!zone) return <div className="container-cnp py-24">A carregar...</div>;

  const canonical = `https://casanoporto.com/zonas/${zone.slug}`;
  const faqSchema = faqPageSchema(zone.faqs);
  const schemas = [
    breadcrumbSchema([
      { name: "Início", url: "https://casanoporto.com/" },
      { name: "Zonas", url: "https://casanoporto.com/zonas" },
      { name: zone.name, url: canonical },
    ]),
    placeSchema(zone, canonical),
  ];
  if (faqSchema) schemas.push(faqSchema);

  return (
    <div data-testid={`page-zona-${zone.slug}`}>
      <SEO
        title={`Comprar casa em ${zone.name}${zone.avg_price_sqm ? ` — preço médio ${fmtEur(zone.avg_price_sqm)}/m²` : ""}`}
        description={
          zone.avg_price_sqm
            ? `Guia de ${zone.name}: preço médio ${fmtEur(zone.avg_price_sqm)}/m² (${zone.avg_price_sqm_source || "índice de mercado"}, ${zone.avg_price_sqm_updated || "actual"}). Imóveis à venda, perfil de comprador e o que precisa de saber antes de comprar. Com a Zélia Silva.`
            : zone.short_intro || `Guia completo de ${zone.name}: perfil, mercado, imóveis à venda e o que precisa de saber antes de comprar. Com a Zélia Silva, consultora local.`
        }
        canonical={canonical}
        image={toImg(zone.main_image)}
        jsonLd={schemas}
      />
      <section className="relative">
        <div className="relative aspect-[16/7] w-full overflow-hidden">
          <img src={toImg(zone.main_image)} alt={`${zone.name} — vista da zona`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 container-cnp pb-10 text-white">
            <span className="tag-chip bg-white/90 text-[#103C4A]">Zona</span>
            <h1 className="mt-4 font-display text-4xl md:text-6xl">{zone.name}</h1>
            <p className="mt-2 max-w-2xl opacity-90">{zone.short_intro}</p>
          </div>
        </div>
      </section>

      {zone.avg_price_sqm ? (
        <section className="container-cnp mt-8 lg:mt-10" data-testid={`zona-market-${zone.slug}`}>
          <div className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="hidden md:flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#103C4A]/10 text-[#103C4A]">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-stone-500">Preço médio no mercado</div>
                <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                  <div className="font-display text-4xl md:text-5xl text-[#103C4A] tracking-[-0.02em]">
                    {fmtEur(zone.avg_price_sqm)}
                  </div>
                  <div className="text-stone-500 text-lg">/ m²</div>
                </div>
                <div className="mt-2 text-sm text-stone-600">
                  Apartamentos à venda em <strong className="text-stone-800 font-medium">{zone.name}</strong>
                  {zone.avg_price_sqm_source ? ` · Fonte: ${zone.avg_price_sqm_source}` : ""}
                  {zone.avg_price_sqm_updated ? ` · ${zone.avg_price_sqm_updated}` : ""}
                </div>
              </div>
            </div>
            <div className="md:text-right">
              <Link to="/avaliacao-gratuita" className="btn-primary">Avaliar a minha casa</Link>
              <div className="mt-2 text-xs text-stone-500">Comparo com o seu imóvel em 24h</div>
            </div>
          </div>

          {Array.isArray(zone.price_by_typology) && zone.price_by_typology.length > 0 && (
            <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-6 md:p-8" data-testid={`zona-typology-${zone.slug}`}>
              <div className="flex items-baseline justify-between gap-6 mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-stone-500">Preço por tipologia</div>
                  <h2 className="mt-2 font-display text-2xl md:text-3xl text-stone-900 tracking-[-0.02em]">€/m² em {zone.name} por T-…</h2>
                </div>
                <div className="hidden md:block text-[10px] uppercase tracking-[0.22em] text-stone-400">
                  {zone.avg_price_sqm_updated || "actual"}
                </div>
              </div>
              <div className="rule-hair mb-5" />
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
                {zone.price_by_typology.map((row) => (
                  <div key={row.typology} className="text-center" data-testid={`zona-typology-${zone.slug}-${row.typology}`}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">{row.typology}</div>
                    <div className="mt-1 font-display text-xl md:text-2xl text-[#103C4A]">{fmtEur(row.price_sqm)}</div>
                    <div className="text-[10px] text-stone-400">/ m²</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs text-stone-500 leading-relaxed">
                Referências indicativas para <strong className="text-stone-700 font-medium">apartamentos à venda</strong> em {zone.name}. Valores unitários mais altos em tipologias menores (T0/T1) e mais baixos em tipologias maiores (T4+) — padrão típico do mercado português.
              </p>
            </div>
          )}
        </section>
      ) : null}

      <section className="container-cnp py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 prose-cnp">
          <h2>Sobre {zone.name}</h2>
          <RichText text={zone.full_description} className="not-prose" />

          {zone.highlights?.length > 0 && (
            <>
              <h3>Destaques da zona</h3>
              <ul>
                {zone.highlights.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </>
          )}

          {zone.buyer_profile && (<><h3>Perfil de comprador</h3><RichText text={zone.buyer_profile} className="not-prose" /></>)}
          {zone.market_notes && (<><h3>Notas de mercado</h3><RichText text={zone.market_notes} className="not-prose" /></>)}

          {zone.faqs?.length > 0 && (
            <>
              <h3>Perguntas frequentes</h3>
              <div className="not-prose space-y-3">
                {zone.faqs.map((f, i) => (
                  <div key={i} className="card-soft p-5">
                    <div className="font-medium text-stone-900">{f.question}</div>
                    <div className="mt-1 text-stone-600 text-sm">{f.answer}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="card-soft p-6 sticky top-24">
            <h3 className="text-lg text-stone-900">Falar com a Zélia sobre {zone.name}</h3>
            <p className="mt-2 text-sm text-stone-600">Sem compromisso — respondo no próprio dia útil.</p>
            <div className="mt-6">
              <ContactForm zone={zone.name} compact testidPrefix={`zona-${zone.slug}-contact`} />
            </div>
          </div>
        </aside>
      </section>

      {properties.length > 0 && (
        <section className="container-cnp py-14 border-t border-stone-200">
          <h2 className="font-display text-3xl md:text-4xl text-stone-900">Imóveis em {zone.name}</h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
